import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Check, Copy, Link2, Loader2, X } from 'lucide-react';
import { SCOPES } from './ShareSessionModal';

/**
 * Every link the signed-in user currently has live, and the switch that turns
 * each one off.
 *
 * Live links only, on purpose: a revoked or expired link is a dead end the
 * reader can do nothing with here (sharing again goes through the session's
 * own Share dialog), so listing them would only bury the links that still
 * matter — the ones someone can still open.
 *
 * Purely presentational: MainContent owns the share list (it also drives the
 * sidebar badges) and refreshes it on open and after every grant or revoke —
 * this component just renders whatever it is handed.
 */

const TITLE_ID = 'shared-links-title';
const COPIED_VISIBLE_MS = 2000;

const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;

/** "stops working in 6 days" — the sentence a sharer actually asks about. */
export const expiryPhrase = (expiresAt) => {
  if (!expiresAt) return 'does not expire';
  const left = expiresAt - Date.now();
  if (left <= 0) return 'has just expired';
  if (left < HOUR_MS) return 'stops working within the hour';
  if (left < DAY_MS) {
    const h = Math.round(left / HOUR_MS);
    return `stops working in ${h} hour${h === 1 ? '' : 's'}`;
  }
  const d = Math.round(left / DAY_MS);
  return `stops working in ${d} day${d === 1 ? '' : 's'}`;
};

const scopeLabel = (scope) =>
  (SCOPES.find((s) => s.value === scope) || SCOPES[0]).label;

function LinkRow({ share, sessionTitle, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const copiedTimer = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const url = `${window.location.origin}/s/${share.token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      clearTimeout(copiedTimer.current);
      setCopied(true);
      copiedTimer.current = setTimeout(() => setCopied(false), COPIED_VISIBLE_MS);
    } catch {
      // Clipboard refused — the row's URL is still reachable via the session's
      // own Share dialog, so stay quiet rather than alarming.
    }
  };

  const revoke = async () => {
    setRevoking(true);
    try {
      await onRevoke(share);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="flex items-start gap-3 border-b border-line py-3 last:border-b-0">
      <Link2 aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-fg-muted" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-fg" title={sessionTitle}>
          {sessionTitle}
        </div>
        <div className="mt-0.5 text-[12.5px] text-fg-secondary">
          {share.public ? (
            <span className="font-semibold text-danger">Anyone with the link</span>
          ) : (
            <span>People signed in to {window.location.host}</span>
          )}
          {' · '}
          {scopeLabel(share.scope)}
          {' · '}
          <span className="text-warning">{expiryPhrase(share.expiresAt)}</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 pt-0.5">
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-raised px-2.5 py-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
        >
          {copied ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : <Copy aria-hidden="true" className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          onClick={revoke}
          disabled={revoking}
          className="flex items-center gap-1.5 rounded-lg border border-danger/40 px-2.5 py-1.5 text-xs font-medium text-danger transition-opacity hover:opacity-80 disabled:opacity-50"
        >
          {revoking && <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />}
          Turn off
        </button>
      </div>
    </div>
  );
}

LinkRow.propTypes = {
  share: PropTypes.shape({
    token: PropTypes.string.isRequired,
    sessionId: PropTypes.string.isRequired,
    public: PropTypes.bool,
    scope: PropTypes.string,
    expiresAt: PropTypes.number,
  }).isRequired,
  sessionTitle: PropTypes.string.isRequired,
  onRevoke: PropTypes.func.isRequired,
};

export default function SharedLinksModal({ open, shares, loading, error, sessions, onRevoke, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Session summaries live in the app's own session list; a share whose
  // session was deleted since keeps working (the grant reads a snapshot), so
  // it still needs a recognisable name here.
  const titleOf = (share) => {
    const session = sessions.find((s) => s.id === share.sessionId);
    return session?.summary || 'A deleted chat';
  };

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="animate-rise w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface"
        style={{ boxShadow: 'var(--shadow-float)' }}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 id={TITLE_ID} className="text-lg font-semibold text-fg">Your Shared Sessions</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close shared sessions dialog"
            className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg"
          >
            <X aria-hidden="true" size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-3">
          {loading && (
            <p className="flex items-center gap-2 py-4 text-sm text-fg-secondary">
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              Checking your links…
            </p>
          )}

          {!loading && error && (
            <p className="py-4 text-sm text-danger">{error}</p>
          )}

          {!loading && !error && shares.length === 0 && (
            <p className="py-6 text-center text-sm text-fg-muted">
              Nothing is shared right now. Share a chat from its row in the sidebar.
            </p>
          )}

          {!loading && !error && shares.map((share) => (
            <LinkRow
              key={share.token}
              share={share}
              sessionTitle={titleOf(share)}
              onRevoke={onRevoke}
            />
          ))}
        </div>

        {!loading && !error && shares.length > 0 && (
          <div className="border-t border-line px-6 py-3 text-xs text-fg-muted">
            Turning a link off is immediate — anyone holding it sees nothing from then on.
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

SharedLinksModal.propTypes = {
  open: PropTypes.bool,
  shares: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  sessions: PropTypes.arrayOf(PropTypes.object),
  onRevoke: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SharedLinksModal.defaultProps = {
  open: false,
  shares: [],
  loading: false,
  error: null,
  sessions: [],
};
